import { useLocation, useNavigate } from 'react-router-dom';
import {
  DND_PRESETS,
  PRESENCE_STATUSES,
  dndPresetMinutes,
  isDndActive,
  type PresenceStatus,
} from '@itsappy/shared';
import { useAuth } from '@/store/auth';
import { toast, useUi } from '@/store/ui';
import { useNotificationSettings, useNotifications, useSetDoNotDisturb } from '@/hooks/queries';
import { useSocket } from '@/realtime/socketContext';
import { api } from '@/lib/api';
import { Avatar, Dropdown, IconButton, MenuItem } from '@/components/ui';
import { STATUS_TEXT } from '@/components/ui/presence';
import { BrandLogo } from '@/components/BrandLogo';
import { AssistantLauncher } from '@/components/assistant';
import {
  BellIcon,
  ChevronDownIcon,
  DarkThemeIcon,
  LanguageIcon,
  LightThemeIcon,
  MenuIcon,
  SearchIcon,
  SignOutIcon,
  PersonIcon,
} from '@/components/ui/icons';
import { cn } from '@/lib/utils';
import { effectiveProductSurface, getProductSurface, hasFullAccess } from '@/lib/product';

const TITLES: [RegExp, string, string][] = [
  [/^\/$/, 'Home', 'الرئيسية'],
  [/^\/spaces$/, 'Spaces', 'المساحات'],
  [/^\/spaces\//, 'Virtual office', 'المكتب الافتراضي'],
  [/^\/projects$/, 'Projects', 'المشاريع'],
  [/^\/projects\//, 'Board', 'لوحة المشروع'],
  [/^\/my-tasks/, 'My tasks', 'مهامي'],
  [/^\/automations/, 'Automations', 'الأتمتة'],
  [/^\/chat/, 'Chat', 'الدردشة'],
  [/^\/calendar/, 'Calendar', 'التقويم'],
  [/^\/meetings/, 'Meetings', 'الاجتماعات'],
  [/^\/whiteboards/, 'Whiteboards', 'السبورات'],
  [/^\/files/, 'Files', 'الملفات'],
  [/^\/mail/, 'Email', 'البريد'],
  [/^\/people/, 'People', 'الأشخاص'],
  [/^\/settings/, 'Settings', 'الإعدادات'],
  [/^\/admin\/mail/, 'Email hosting', 'استضافة البريد'],
  [/^\/admin\/workspaces\/all/, 'Workspaces', 'مساحات العمل'],
  [/^\/admin\/workspaces/, 'Workspace requests', 'طلبات مساحات العمل'],
  [/^\/admin/, 'Administration', 'الإدارة'],
];

const STATUS_DOT: Record<PresenceStatus, string> = {
  online: 'bg-emerald-400',
  busy: 'bg-red-400',
  away: 'bg-amber-400',
  in_meeting: 'bg-violet-400',
  offline: 'bg-slate-500',
};

const STATUS_AR: Record<PresenceStatus, string> = {
  online: 'متاح',
  busy: 'مشغول',
  away: 'بالخارج',
  in_meeting: 'في اجتماع',
  offline: 'الظهور دون اتصال',
};

export function Topbar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, features, disabledFeatures, setUser, logout } = useAuth();
  const {
    theme,
    language,
    toggleTheme,
    toggleLanguage,
    toggleSidebar,
    setCommandPalette,
    setNotifications,
  } = useUi();
  const { socket, connected } = useSocket();
  const { data } = useNotifications();
  const product = effectiveProductSurface(getProductSurface(), features);
  const fullAccess = hasFullAccess(features, disabledFeatures);

  const arabic = language === 'ar';
  const titleRow = TITLES.find(([re]) => re.test(pathname));
  const title = titleRow?.[arabic ? 2 : 1] ?? 'Itsappy Lab';
  const unread = data?.unreadCount ?? 0;
  const status = user?.status ?? 'online';
  const statusLabel = arabic ? STATUS_AR[status] : STATUS_TEXT[status];
  const setDnd = useSetDoNotDisturb();
  /* Two things can pause notifications — this menu and the composer's `/dnd`
     — and they write different caches. Reading the settings query first, with
     the signed-in user as the fallback that paints before it resolves, is
     what keeps the moon honest whichever one was used. */
  const { data: notificationSettings } = useNotificationSettings();
  const dndUntil = notificationSettings?.dnd?.until ?? user?.dndUntil;
  const paused = isDndActive(dndUntil);

  async function setStatus(next: PresenceStatus) {
    socket?.emit('presence:set', { status: next });
    const updated = await api.patch<typeof user>('/users/me', { status: next });
    if (updated) setUser(updated);
  }

  function pause(minutes: number | null) {
    setDnd.mutate(minutes, {
      onError: (error) =>
        toast.error(
          arabic ? 'تعذّر الحفظ' : 'Could not pause',
          error instanceof Error ? error.message : undefined,
        ),
    });
  }

  /* "Until I turn it off" is stored as a date years away; showing that date
     would read as a clock fault, so it is named for what it is instead. */
  const indefinite =
    paused && new Date(dndUntil!).getTime() - Date.now() > 365 * 86_400_000;
  const pausedUntil = paused
    ? indefinite
      ? arabic
        ? 'حتى تُعيد تشغيلها'
        : 'until you turn it off'
      : new Date(dndUntil!).toLocaleTimeString(arabic ? 'ar' : undefined, {
          hour: 'numeric',
          minute: '2-digit',
        })
    : null;

  async function signOut() {
    await logout();
    navigate('/login');
  }

  return (
    <header className={cn(
      'flex shrink-0 items-center gap-2 border-b border-surface-2 bg-surface-1 px-3 sm:gap-3',
      product === 'lab' ? 'h-14' : 'h-[4.5rem] sm:h-20 sm:px-5',
    )}>
      {product === 'lab' && <IconButton
        label={arabic ? 'تبديل الشريط الجانبي' : 'Toggle sidebar'}
        onClick={toggleSidebar}
      >
        <MenuIcon />
      </IconButton>}

      {product === 'lab' ? (
        <h1 className="min-w-0 truncate text-sm font-semibold text-ink">{title}</h1>
      ) : (
        <BrandLogo className="h-12 w-48 sm:h-14 sm:w-60" />
      )}

      {/* Everything past this point belongs to the bar's trailing edge, and the
          spacer owns that job on its own. Hanging `ml-auto` off the search
          control instead left the whole cluster sliding back to the left on
          any account that does not get search. */}
      <span className="ml-auto" />

      {/* The full search control needs room; below `md` it collapses to its icon. */}
      {product === 'lab' && fullAccess && <button
        type="button"
        onClick={() => setCommandPalette(true)}
        className="hidden w-full max-w-xs items-center gap-2 rounded-lg border border-surface-3 bg-surface-2 px-3 py-1.5 text-left text-sm text-slate-400 transition-colors hover:border-surface-4 hover:text-slate-200 md:flex"
      >
        <SearchIcon size="sm" />
        <span className="flex-1 truncate">
          {arabic ? 'ابحث في المهام والأشخاص والرسائل…' : 'Search tasks, people, messages…'}
        </span>
        <kbd className="shrink-0 rounded border border-surface-4 px-1 text-[10px] text-slate-400">
          ⌘K
        </kbd>
      </button>}
      {product === 'lab' && fullAccess && <IconButton
        label={arabic ? 'بحث' : 'Search'}
        onClick={() => setCommandPalette(true)}
        className="md:hidden"
      >
        <SearchIcon />
      </IconButton>}

      {/* Gated on the product itself, not on the full-workspace surface: the
          server checks `assistant` on every call behind this button, so an
          account holding the surface without the product got a launcher that
          opened onto a 403. */}
      {features.includes('assistant') && <AssistantLauncher />}

      {/**
       * One control for "how am I showing up": the chosen presence status, with
       * the realtime connection folded into the same dot rather than sitting
       * beside it as a second, unlabelled light.
       */}
      <Dropdown
        label={arabic ? 'تغيير الحالة' : 'Change your status'}
        triggerClassName="w-auto"
        trigger={
          <span className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg px-2 text-sm text-slate-300 transition-colors hover:bg-surface-3">
            <span
              className={cn(
                'h-2.5 w-2.5 shrink-0 rounded-full',
                connected ? STATUS_DOT[status] : 'animate-pulse bg-amber-400',
              )}
            />
            {/* A pause is worn beside the status rather than replacing it:
                you are still "Available" to the people who can see you, you
                just will not be buzzed. */}
            {paused && (
              <span className="text-indigo-300" title={arabic ? 'الإشعارات متوقفة' : 'Notifications paused'}>
                <DarkThemeIcon size="xs" />
              </span>
            )}
            <span className="hidden max-w-28 truncate sm:block">
              {connected ? statusLabel : arabic ? 'إعادة الاتصال…' : 'Reconnecting…'}
            </span>
            <span className="text-slate-500">
              <ChevronDownIcon size="sm" />
            </span>
          </span>
        }
      >
        {(close) => (
          <>
            {PRESENCE_STATUSES.map((option) => (
              <MenuItem
                key={option}
                selected={option === status}
                onClick={() => {
                  close();
                  void setStatus(option);
                }}
              >
                <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', STATUS_DOT[option])} />
                {arabic ? STATUS_AR[option] : STATUS_TEXT[option]}
              </MenuItem>
            ))}
            {/* Pausing lives under status because it is the same question —
                "how am I showing up" — answered for the phone in your pocket
                rather than the dot on your avatar. */}
            <div className="my-1 border-t border-surface-3" />
            <p className="flex items-center gap-1.5 px-3 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <DarkThemeIcon size="xs" />
              {arabic ? 'إيقاف الإشعارات مؤقتاً' : 'Pause notifications'}
            </p>
            {pausedUntil && (
              <p className="px-3 pb-1 text-xs text-indigo-300">
                {arabic
                  ? `متوقفة ${indefinite ? '' : 'حتى '}${pausedUntil}`
                  : `Paused ${indefinite ? '' : 'until '}${pausedUntil}`}
              </p>
            )}
            {DND_PRESETS.map((preset) => (
              <MenuItem
                key={preset.key}
                disabled={setDnd.isPending}
                onClick={() => {
                  close();
                  pause(dndPresetMinutes(preset.key));
                }}
              >
                <span className="w-2.5 shrink-0" />
                {preset.label}
              </MenuItem>
            ))}
            {paused && (
              <MenuItem
                disabled={setDnd.isPending}
                onClick={() => {
                  close();
                  pause(null);
                }}
              >
                <span className="w-2.5 shrink-0" />
                {arabic ? 'إيقاف الإيقاف المؤقت' : 'Turn off'}
              </MenuItem>
            )}
          </>
        )}
      </Dropdown>

      {/* Appearance and language are preferences, not navigation — they sit
          together behind a divider, and step aside on narrow screens where the
          sidebar's account menu carries them instead. */}
      <div className="hidden items-center gap-1 border-l border-surface-3 pl-2 sm:flex">
        <IconButton
          label={
            theme === 'dark'
              ? arabic
                ? 'استخدام المظهر الفاتح'
                : 'Use light theme'
              : arabic
                ? 'استخدام المظهر الداكن'
                : 'Use dark theme'
          }
          onClick={toggleTheme}
        >
          {theme === 'dark' ? <LightThemeIcon /> : <DarkThemeIcon />}
        </IconButton>
        {/* Names the language it switches *to*, so the control is self-explaining
            — a globe on its own never says which way it goes. */}
        <button
          type="button"
          onClick={toggleLanguage}
          aria-label={arabic ? 'Switch to English' : 'التبديل إلى العربية'}
          title={arabic ? 'Switch to English' : 'التبديل إلى العربية'}
          className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg px-2 text-slate-300 transition-colors hover:bg-surface-3 hover:text-ink"
        >
          <LanguageIcon />
          <span aria-hidden className="text-xs font-semibold leading-none">
            {arabic ? 'EN' : 'ع'}
          </span>
        </button>
      </div>

      {product !== 'lab' && user && (
        <Dropdown
          align="right"
          className="w-60"
          label={arabic ? 'الملف الشخصي' : 'Profile menu'}
          trigger={
            <span className="inline-flex h-10 items-center gap-2 rounded-lg px-1.5 transition-colors hover:bg-surface-2">
              <Avatar user={user} size="sm" showStatus />
              <span className="hidden max-w-32 truncate text-sm font-medium text-ink lg:block">
                {user.name}
              </span>
              <span className="text-slate-500"><ChevronDownIcon size="sm" /></span>
            </span>
          }
        >
          {(close) => (
            <>
              <div className="border-b border-surface-3 px-3 py-2.5">
                <p className="truncate text-sm font-medium text-ink">{user.name}</p>
                <p className="truncate text-xs text-slate-400">{user.email}</p>
              </div>
              <MenuItem
                onClick={() => {
                  close();
                  window.location.assign('https://lab.itsappy.com/settings');
                }}
              >
                <PersonIcon size="sm" />
                {arabic ? 'الملف الشخصي والإعدادات' : 'Profile & settings'}
              </MenuItem>
              <MenuItem onClick={toggleTheme}>
                {theme === 'dark' ? <LightThemeIcon size="sm" /> : <DarkThemeIcon size="sm" />}
                {theme === 'dark'
                  ? arabic ? 'استخدام المظهر الفاتح' : 'Switch to light theme'
                  : arabic ? 'استخدام المظهر الداكن' : 'Switch to dark theme'}
              </MenuItem>
              <MenuItem onClick={toggleLanguage}>
                <LanguageIcon size="sm" />
                {arabic ? 'English' : 'العربية'}
              </MenuItem>
              <div className="my-1 border-t border-surface-3" />
              <MenuItem
                onClick={() => {
                  close();
                  void signOut();
                }}
              >
                <SignOutIcon size="sm" />
                {arabic ? 'تسجيل الخروج' : 'Sign out'}
              </MenuItem>
            </>
          )}
        </Dropdown>
      )}

      {product === 'lab' && <div className="relative">
        <IconButton
          label={
            unread > 0
              ? `${arabic ? 'الإشعارات' : 'Notifications'} (${unread})`
              : arabic
                ? 'الإشعارات'
                : 'Notifications'
          }
          onClick={() => setNotifications(true)}
        >
          <BellIcon />
        </IconButton>
        {unread > 0 && (
          <span className="badge-count pointer-events-none absolute -right-0.5 -top-0.5 bg-red-500 ring-2 ring-surface-1">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </div>}
    </header>
  );
}
